import { Controller } from "@hotwired/stimulus"

// Flash toasts (shared/_notifications, rendered inside the layout's
// turbo_frame_tag "notice" and re-sent by `turbo_stream.update "notice"`).
//
// Each toast is animated and armed from `notificationTargetConnected`, NOT from
// `connect()`. A target callback fires for targets present when the controller
// connects AND for every target inserted into the element afterwards, which is
// the only hook that covers every path a toast can arrive by:
//   - first paint / a Turbo Drive visit  → the controller connects with the
//     toast already inside it,
//   - a `turbo_stream.update "notice"`   → the controller element survives and
//     only its children are replaced, so `connect()` never fires again.
// Binding this to `connect()` would silently skip the second case.
//
// (This app does not enable `turbo-refresh-method: morph` in this milestone. A
// morph refresh is a third instance of the same shape — element survives,
// children are replaced — so this design is already correct if morph is turned
// on later. That is why it is written this way, not because morph is on.)
//
// Timers are kept per toast in a Map rather than as one instance field: two
// flashes can be on screen at once (notice + alert) and each dismisses on its
// own schedule.
export default class extends Controller {
  static targets = ["notification"]
  static values = {
    delay: { type: Number, default: 6000 }
  }

  initialize() {
    this.hideTimeouts = new Map()
  }

  disconnect() {
    this.hideTimeouts.forEach((timeout) => clearTimeout(timeout))
    this.hideTimeouts.clear()
  }

  notificationTargetConnected(toast) {
    this.show(toast)
  }

  notificationTargetDisconnected(toast) {
    clearTimeout(this.hideTimeouts.get(toast))
    this.hideTimeouts.delete(toast)
  }

  // Longer than the leave transition (300 ms) with room to spare.
  static LEAVE_TIMEOUT_MS = 600

  // The transition class lists are author-supplied data attributes. A toast
  // rendered from different markup — a turbo_stream append, or a plain flash
  // div reusing `data-notification-target="notification"` — has none of them,
  // and `undefined.split` would throw inside the Stimulus target callback,
  // which also aborts before the auto-dismiss timer is armed.
  tokens(toast, name) {
    return (toast.dataset[name] || "").split(/\s+/).filter(Boolean)
  }

  show(toast) {
    // The toast renders visible (no `hidden` class) so correctness never
    // depends on this callback firing. This only plays the entrance animation
    // and arms the auto-dismiss timer.
    const enterFrom = this.tokens(toast, "transitionEnterFrom")
    const enterTo = this.tokens(toast, "transitionEnterTo")

    toast.classList.add(...enterFrom)
    requestAnimationFrame(() => {
      toast.classList.remove(...enterFrom)
      toast.classList.add(...enterTo)
    })

    if (this.delayValue > 0) {
      this.hideTimeouts.set(toast, setTimeout(() => this.hide(toast), this.delayValue))
    }
  }

  // `data-action="notification#hide"` on a toast's close button, or called with
  // the toast element directly by the auto-hide timer. Hides THAT toast — with
  // two flashes on one page the close button used to dismiss the first one
  // regardless of which was clicked.
  hide(eventOrToast) {
    let toast = eventOrToast
    if (eventOrToast instanceof Event) {
      eventOrToast.preventDefault()
      toast = eventOrToast.target.closest("[data-notification-target='notification']")
    }
    if (!toast) return

    clearTimeout(this.hideTimeouts.get(toast))
    this.hideTimeouts.delete(toast)

    const leaveFrom = this.tokens(toast, "transitionLeaveFrom")
    const leaveTo = this.tokens(toast, "transitionLeaveTo")

    toast.classList.remove(...leaveFrom)
    toast.classList.add(...leaveTo)

    // Belt and braces, because a toast that is never removed is not merely an
    // invisible node: the stack is `flex-col-reverse`, so each orphan pushes
    // later toasts further off screen.
    //   • `e.target !== toast` — transitionend bubbles, so any descendant with
    //     a transition (a hover-animated close button, a progress bar) would
    //     otherwise remove the toast mid-animation.
    //   • the timer — if the transition never runs at all (prefers-reduced-
    //     motion zeroing durations, a display:none ancestor, leave classes that
    //     change no animatable property) transitionend never fires.
    const remove = () => toast.remove()
    const fallback = setTimeout(remove, this.constructor.LEAVE_TIMEOUT_MS)
    toast.addEventListener("transitionend", (e) => {
      if (e.target !== toast) return
      clearTimeout(fallback)
      remove()
    })
  }
}
